<template>
  <div>
    <table class="table mt-0 mb-0 full-width">
      <thead class="bg-light">
        <tr>
          <th scope="col" class="border-0">{{ $t('order.number') }}</th>
          <th scope="col" class="border-0">{{ $t('status.status') }}</th>
          <th scope="col" class="border-0">{{ $t('order.dateOfIssue') }}</th>
          <th scope="col" class="border-0">{{ $t('order.paymentDeadline') }}</th>
          <th scope="col" class="border-0">{{ $t('order.paymentDate') }}</th>
          <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, index) of reversedSettlements"
          :key="'status-' + index">
          <td class="align-middle">
            {{ item.number }}<br>
            <small v-if="userIsAdmin">
              {{ $t('settlements.reminders.status') }}:
              <span
                v-if="item.exclude_from_invoice_reminder"
                class="text-danger">
                {{ $t('settlements.reminders.inactive') }}
              </span>
              <span
                v-else
                class="text-success">
                {{ $t('settlements.reminders.active') }}
              </span>
              <a
                v-if="item.exclude_from_invoice_reminder && !remindersChangeInProgress"
                href="#"
                class="text-primary"
                @click.prevent="invoiceRemindersToggle('off', item.id)">
                ({{ $t('settlements.reminders.enable') }})
              </a>
              <a
                v-else-if="!remindersChangeInProgress"
                class="text-primary"
                href="#"
                @click.prevent="invoiceRemindersToggle('on', item.id)">
                ({{ $t('settlements.reminders.disable') }})
              </a>
              <span v-else-if="remindersChangeInProgress === item.id">
                <span class="spinner-border spinner-border-sm" role="status"></span>
              </span>
            </small>
          </td>
          <td class="align-middle">
            <d-badge :theme="item.status === 'PAID' ? 'success' : 'danger'">
              {{ getStatusName(item.status) }}
            </d-badge>

            <template v-if="userIsAdmin">
              <br>
              <small
                v-if="item.status === 'PAID'"
                :style="statusChangeInProgress ? 'pointer-events: none; opacity: .25;' : ''">
                <a
                  href="#"
                  @click.prevent="changePaymentStatus(item.id, 'UNPAID')">
                  <template v-if="statusChangeInProgress !== item.id">
                    {{ $t('settlements.changeToUnpaid') }}
                  </template>
                  <div v-else class="spinner-border spinner-border-sm" role="status"></div>
                </a>
              </small>
              <small
                v-else-if="item.status === 'UNPAID'"
                :style="statusChangeInProgress ? 'pointer-events: none; opacity: .25;' : ''">
                <a
                  href="#"
                  @click.prevent="changePaymentStatus(item.id, 'PAID')">
                  <template v-if="statusChangeInProgress !== item.id">
                    {{ $t('settlements.changeToPaid') }}
                  </template>
                  <div v-else class="spinner-border spinner-border-sm" role="status"></div>
                </a>
              </small>
            </template>
          </td>
          <td class="align-middle">
            {{ item.invoice_date }}
          </td>
          <td class="align-middle">
            {{ item.payment_date }}
          </td>
          <td class="align-middle">
            {{ item.settlement_date }}
          </td>
          <td class="align-middle" style="width: 240px;">
            <i
              v-if="!item.file || !item.file_url"
              class="material-icons"
              :title="$t('settlements.waitingForInvoice')"
              style="cursor: help">
              schedule
            </i>
            <d-button
              v-else
              theme="primary"
              @click.prevent="downloadInvoice(item.id, item.number)">
              <template v-if="!downloadInProgress">
                <i class="material-icons">cloud_download</i>
                {{ $t('order.download') }}
              </template>
              <template v-if="downloadInProgress">
                <div
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
              </template>
            </d-button>

            <d-button
              v-if="item.can_delete_invoice || userIsAdmin"
              theme="danger"
              :disabled="!!deleteInProgress || item.is_correction"
              @click.prevent="deleteInvoice(item.id)"
              class="ml-2"
              :title="$t('settlements.deleteInvoiceRequest')">
              <template v-if="deleteInProgress !== item.id"><i class="material-icons">delete</i></template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </d-button>

            <d-button
              v-if="item.can_generate_printout"
              theme="warning"
              :disabled="!!printoutInProgress"
              @click.prevent="printoutInvoice(item.id)"
              class="ml-2"
              :title="$t('settlements.invoicePrintout')">
              <template v-if="printoutInProgress !== item.id"><i class="material-icons">print</i></template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </d-button>

            <d-button
              v-if="userIsAdmin"
              theme="secondary"
              class="ml-2"
              @click.prevent="loadHistory(item.id)"
              :title="$t('settlements.viewHistory')">
              <i class="material-icons">info</i>
            </d-button>
          </td>
        </tr>
        <tr v-if="!settlements.length">
          <td colspan="6">
            <p class="text-center">
              {{ $t('order.noInvoicesConnectedToOrder') }}&hellip;
            </p>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td
            colspan="6"
            class="text-center">
            <d-button
              v-if="userIsAdmin"
              theme="success"
              @click.prevent="addSettlement">
              {{ $t('settlements.addSettlement') }}
            </d-button>
          </td>
        </tr>
      </tfoot>
    </table>

    <settlements-history-popup
      v-if="displaySettlementsHistoryPopup"
      :settlementID="selectedSettlement" />

    <settlements-add-popup
      v-if="displaySettlementsAddPopup"
      :orderID="orderID"
      :customerID="customerID" />
  </div>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import settlementsStatuses from './../../data/settlements-statuses.js';
import SettlementsAddPopup from './../../components/settlements/SettlementsAddPopup.vue';
import SettlementsHistoryPopup from './../../components/settlements/SettlementsHistoryPopup.vue';
import Vue from 'vue';

export default {
  name: 'order-settlements',
  props: [
    'orderID',
    'customerID',
    'orderName',
    'settlementsData'
  ],
  mixins: [
    CustomerPanel
  ],
  components: {
    'settlements-add-popup': SettlementsAddPopup,
    'settlements-history-popup': SettlementsHistoryPopup
  },
  computed: {
    reversedSettlements () {
      return JSON.parse(JSON.stringify(this.settlements)).reverse();
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    }
  },
  data () {
    return {
      settlements: [],
      deleteInProgress: false,
      downloadInProgress: false,
      printoutInProgress: false,
      displaySettlementsHistoryPopup: false,
      selectedSettlement: false,
      statusChangeInProgress: false,
      displaySettlementsAddPopup: false,
      loadedSettlementsData: false,
      remindersChangeInProgress: false
    };
  },
  mounted () {
    this.settlements = this.settlementsData;
    this.$bus.$on('settlements-reload-needed', this.loadSettlements);
    this.$bus.$on('close-settlement-history-popup', this.closeHistory);
    this.$bus.$on('close-settlement-add-popup', this.closeAddPopup);
  },
  methods: {
    getStatusName (status) {
      return ListUtils.getKeyLabel(settlementsStatuses[this.$i18n.locale], status);
    },
    // eslint-disable-next-line
    downloadInvoice (invoiceID, invoiceNumber) {
      if (this.downloadInProgress) {
        return;
      }

      this.downloadInProgress = true;

      this.$http.post(this.getApiEndpoint('/settlements/download-file/' + parseInt(invoiceID, 10)), {}, {
        responseType: 'blob'
      }).then((response) => {
        let link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([response.data]));
        let fileName = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        fileName = fileName.replace(/[\s\/\\]/gmi, '-', fileName);
        link.setAttribute('download', fileName.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadInProgress = false;
      }).catch(() => {
        this.downloadInProgress = false;
      });
    },
    deleteInvoice (settlementID) {
      this.deleteInProgress = settlementID;

      this.$http.post(this.getApiEndpoint('/settlements/delete/' + settlementID)).then(response => {
        if (response.data.success) {
          Vue.swal({
            title: this.$t('settlements.invoiceRollbackTitle'),
            html: this.$t('settlements.invoiceRollbackDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        } else {
          Vue.swal({
            title: this.$t('settlements.errorOccuredDuringRollback'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }

        this.deleteInProgress = false;
        this.$bus.$emit('settlements-reload-needed');
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringSending'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        this.deleteInProgress = false;
      });
    },
    printoutInvoice (settlementID) {
      this.printoutInProgress = settlementID;

      this.$http.post(this.getApiEndpoint('/settlements/generate-printout/' + settlementID)).then(response => {
        if (response.data.success) {
          Vue.swal({
            title: this.$t('settlements.invoicePrintoutTitle'),
            html: this.$t('settlements.invoicePrintoutDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        } else {
          Vue.swal({
            title: this.$t('settlements.errorOccuredDuringPrintout'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }

        this.printoutInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringSending'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        this.printoutInProgress = false;
      });
    },
    loadHistory (settlementID) {
      this.selectedSettlement = settlementID;
      this.displaySettlementsHistoryPopup = true;
    },
    closeHistory () {
      this.selectedSettlement = false;
      this.displaySettlementsHistoryPopup = false;
    },
    changePaymentStatus (settlementID, newStatus) {
      this.statusChangeInProgress = settlementID;

      this.$http.post(this.getApiEndpoint('/settlements/change-status/' + settlementID), {
        status: newStatus
      }).then(response => {
        if (response.data.success) {
          Vue.swal({
            title: this.$t('settlements.statusChangedTitle'),
            html: this.$t('settlements.statusChangedDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.loadSettlements();
        } else {
          Vue.swal({
            title: this.$t('ui.errorOccured'),
            html: this.$t('ui.errorOccuredDesc'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }

        this.statusChangeInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccured'),
          html: this.$t('ui.errorOccuredDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        this.statusChangeInProgress = false;
      });
    },
    addSettlement () {
      this.displaySettlementsAddPopup = true;
    },
    closeAddPopup () {
      this.displaySettlementsAddPopup = false;
    },
    loadSettlements () {
      FormUtils.loadItemData(this, {
        endpointBase: this.getApiEndpoint('/orders/item/'),
        instanceFields: ['settlements'],
        method: 'get',
        loadedKey: 'loadedSettlementsData',
        errorKey: 'loadError',
        params: {
          with: [
            'settlements'
          ]
        }
      });
    },
    invoiceRemindersToggle (newState, settlementID) {
      this.remindersChangeInProgress = settlementID;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/settlements/update-exclude-from-invoice-reminder/' + settlementID,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        formData: {
          exclude_from_invoice_reminder: newState === 'on' ? 1 : 0
        },
        successAction: () => {
          this.remindersChangeInProgress = false;
          this.$bus.$emit('settlements-reload-needed');
        },
        failAction: () => {
          this.remindersChangeInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('settlements-reload-needed', this.loadSettlements);
    this.$bus.$off('close-settlement-history-popup', this.closeHistory);
    this.$bus.$off('close-settlement-add-popup', this.closeAddPopup);
  }
}
</script>
